import { useCallback, useRef } from 'react';

type ContentReadyCallback = () => void;

export type ContentReadySignal = {
	onContentReady: ContentReadyCallback;
	resetContentReady: () => void;
	waitForContentReady: (callback: ContentReadyCallback) => () => void;
};

/**
 * Coordinates a resolved entry point with the overlay behavior waiting for it.
 *
 * Entry point containers are passed to overlays as static React nodes, so their
 * consumers cannot rely on parent state rerenders to observe readiness. The
 * mutable signal preserves readiness across mount ordering and can be reset for
 * each newly opened overlay.
 */
export function useContentReadySignal(): ContentReadySignal {
	const isContentReadyRef = useRef(false);
	const onContentReadyRef = useRef<ContentReadyCallback | null>(null);

	const resetContentReady = useCallback(() => {
		isContentReadyRef.current = false;
		onContentReadyRef.current = null;
	}, []);

	const onContentReady = useCallback(() => {
		isContentReadyRef.current = true;
		const callback = onContentReadyRef.current;
		onContentReadyRef.current = null;
		callback?.();
	}, []);

	const waitForContentReady = useCallback((callback: ContentReadyCallback) => {
		if (isContentReadyRef.current) {
			callback();
			return () => {};
		}

		onContentReadyRef.current = callback;
		return () => {
			if (onContentReadyRef.current === callback) {
				onContentReadyRef.current = null;
			}
		};
	}, []);

	return { onContentReady, resetContentReady, waitForContentReady };
}
